import { HoverCard, Text, Tooltip } from "@mantine/core";
import clsx from "clsx";
import { type JSX, useEffect } from "react";

import { useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useStopwatch } from "@/shared-utils";

import type { TaskProps } from "../../types";

import { useIsPacingActive } from "../../hooks/useIsPacingActive";
import { usePacingState } from "../../hooks/usePacingState";
import classes from "./CallDuration.module.css";
import { PacingPopoverContent } from "./PacingPopover";

function PlainTimer({ formattedTime, totalLabel }: { formattedTime: string; totalLabel: string }): JSX.Element {
    return (
        <Tooltip label={totalLabel} position="bottom" zIndex="var(--mantine-priority-higher)">
            <Text className={classes.timerText} component="span" size="sm" ff="monospace">
                {formattedTime}
            </Text>
        </Tooltip>
    );
}

export function CallDuration({ task }: TaskProps): JSX.Element {
    const logger = useLogger({ module: "CallDuration" });
    const isPacingActive = useIsPacingActive();
    const taskAssignedAt = task.assignedAt;
    const { startedTimestamp, kind, id: sessionId } = useSession();
    const formattedTime = useStopwatch(taskAssignedAt?.getTime());
    const formattedSessionDuration = useStopwatch(startedTimestamp);
    const callActive = task.status === "assigned";
    const totalLabel = `Total ${kind === "with-customer" ? formattedSessionDuration : "--"}`;

    const pacing = usePacingState({
        partner: task.partner,
        sessionId,
        startedTimestamp,
        callActive,
        isPacingActive,
    });

    useEffect(() => {
        if (task.status !== "assigned" && kind !== "with-customer") {
            return;
        }
        if (!taskAssignedAt) {
            logger.warn(
                {
                    task,
                    callTime: formattedTime,
                },
                "UNDEFINED TASK ASSIGNMENT DATE",
            );
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [task.status, taskAssignedAt]);

    if (!isPacingActive) {
        return <PlainTimer formattedTime={formattedSessionDuration} totalLabel={totalLabel} />;
    }

    const pillContent = (
        <div className={clsx(classes.pill, classes[pacing.state])} data-testid="pacing-pill">
            <Text className={classes.timerText} component="span" size="sm" ff="monospace">
                {formattedSessionDuration}
            </Text>
        </div>
    );

    if (pacing.hasTarget) {
        return (
            <HoverCard
                position="bottom"
                withArrow
                shadow="md"
                withinPortal
                openDelay={200}
                closeDelay={100}
                zIndex="var(--mantine-priority-higher)"
            >
                <HoverCard.Target>{pillContent}</HoverCard.Target>
                <HoverCard.Dropdown>
                    <PacingPopoverContent pacing={pacing} />
                </HoverCard.Dropdown>
            </HoverCard>
        );
    }

    return (
        <Tooltip label={totalLabel} position="bottom" zIndex="var(--mantine-priority-higher)">
            {pillContent}
        </Tooltip>
    );
}
